import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import { UserState } from "redux-oidc";
import { Icon } from "@cadmakers-ui/icons";
import FeedbackCategoryBadge from "../Common/FeedbackBadges/FeedbackCategoryBadge";
import FeedbackSizeBadge from "../Common/FeedbackBadges/FeedbackSizeBadge";
import {
  FeedbackState,
  FeedbackCategory,
  FeedbackVote,
  FeedbackParent,
  FeedbackSize
} from "../../Constants/feedbackEnums";
import { appPaths } from "../../../Common/Constants/appPaths";
import VoteOnFeedbackButton from "../Common/VoteOnFeedbackButton/VoteOnFeedbackButton";
import AuthorProfileBadge from "../../../User/Components/Common/AuthorProfileBadge";
import FeedbackAppBadge from "../Common/FeedbackBadges/FeedbackAppBadge";
import FeedbackStateChangeButtons from "../Common/FeedbackButtons/FeedbackStateChangeButtons";
import { Feedback } from "../..";
import { RelationshipDisplayFeedback, FeatureRequest } from "../../Models/Feedback";
import FeedbackTopicBadge from "../Common/FeedbackBadges/FeedbackTopicBadge";
import { DateDisplay } from "../../../Common";
import { FeedbackHistoryModal } from "../FeedbackHistory/FeedbackHistoryModal";
import MoveFeedbackModal from "../Common/MoveFeedbackModal";
import styles from "../../Styles/FeedbackDetails.module.scss";
import FeedbackPrioritySetterContainer from "../Common/FeedbackPrioritySetter/FeedbackPrioritySetterContainer";
import FeedbackForceTransitionStateButton from "../Common/FeedbackForceTransitionState/FeedbackForceTransitionStateButton";
import FeedbackWorkflowStateBadge from "../Common/FeedbackBadges/FeedbackWorkflowStateBadge";
import { TagDisplay } from "../../../Tags";
import FeedbackAssignmentsDisplay from "../Common/FeedbackAssignment/FeedbackAssignmentsDisplay";
import { User, userService } from "../../../User";
import { DueDateDisplayContainer } from "../Common/DueDateDisplay/DueDateDisplayContainer";
import iconStyles from "../../../Common/Styles/Icons.module.scss";
import { RelationshipFeedbackDisplayContainer } from "../Common/ParentFeedbackDisplay/RelationshipFeedbackDisplayContainer";
import { Application } from "../../../App/Models/Application";
import { Topic } from "../../../Topic/Models/Topic";

type Props = {
  feedback: Feedback;
  user: UserState;
  activeApp: Application;
  activeTopic: Topic;
  paramsCode: string;
  onVote?: (feedbackCode: string, vote: FeedbackVote) => void;
  onStateChanged?: (feedbackCode: string) => void;
  onMoveFeedback?: (feedback: Feedback) => void;
  onAssignedUsersChange?: (users: User[]) => void;
  onDueDateChange?: (newDueDate: Date | undefined) => void;
  onRelationshipChange?: () => void;
  static?: boolean;
};

export const FeedbackDetails: React.FC<Props> = props => {
  const location = useLocation();
  const history = useHistory();

  const [isMoveFeedbackModalOpen, setIsMoveFeedbackModalOpen] = useState(false);
  const isCMUser = userService.isUserCadmakers(props.user);
  const isFeatureRequest = props.feedback.category === FeedbackCategory.FeatureRequest;

  const getChildrenDisplay = () => {
    const childrenDisplay: RelationshipDisplayFeedback[] = [];
    if (props.feedback.children) {
      const { children } = props.feedback;
      children.forEach(child => {
        if (child.isActive) {
          childrenDisplay.push({ value: child.id, label: child.title });
        }
      });
    }
    return childrenDisplay;
  };

  const getParentDisplay = () => {
    if (props.feedback.parentFeedback) {
      return {
        value: props.feedback.parentFeedback?.id,
        label: props.feedback.parentFeedback?.title
      };
    }
    return undefined;
  };

  const [linkPath, setLinkPath] = useState("");
  const [linkText, setLinkText] = useState("");

  const feedbackApplicationPrefix = props.feedback?.application?.rolePrefix;
  const feedbackTopicCode = props.feedback?.topic?.code;
  const activeTopicCode = props.activeTopic?.code;
  const activeAppPrefix = props.activeApp?.rolePrefix;
  const feedbackParent = props.feedback?.parent;

  useEffect(() => {
    if (
      feedbackParent === FeedbackParent.Application &&
      activeAppPrefix &&
      feedbackApplicationPrefix
    ) {
      if (location.pathname.includes("/featurerequests/")) {
        setLinkText("All Feature Requests");
        setLinkPath(appPaths.app.featureRequests.replace(":id", activeAppPrefix));
      }
      if (location.pathname.includes("/bugreports/")) {
        setLinkText("All Bug Reports");
        setLinkPath(appPaths.app.bugReports.replace(":id", activeAppPrefix));
      }
      if (location.pathname.includes("/resourcerequests/")) {
        setLinkText("All Resource Requests");
        setLinkPath(appPaths.app.resourceRequests.replace(":id", activeAppPrefix));
      }
    }
    if (feedbackParent === FeedbackParent.Topic && feedbackTopicCode) {
      setLinkText(props.feedback.topic.name);
      setLinkPath(appPaths.topic.featureRequests.replace(":topicCode", feedbackTopicCode));
    }
  }, [
    feedbackParent,
    activeAppPrefix,
    feedbackApplicationPrefix,
    activeTopicCode,
    feedbackTopicCode
  ]);

  return (
    <Container fluid>
      {!props.static && (
        <>
          <Row>
            <Col>
              <Link to={linkPath}>
                <span style={{ color: "black" }}>
                  <Icon
                    appName="GeneralLayout"
                    assetName="GENERAL_L_ArrowHead-L"
                    className={iconStyles.smallIcon}
                  />
                  <span>&nbsp;</span>
                  {linkText}
                </span>
              </Link>
            </Col>
          </Row>
          <br />
        </>
      )}

      <Row>
        {!props.static && !!props.onVote && (
          <Col xs="auto">
            <VoteOnFeedbackButton
              feedbackCode={props.feedback.code}
              existingVote={props.feedback.loggedInUserVote}
              voteCount={props.feedback.voteCount}
              onVote={props.onVote}
              vertical
            />
          </Col>
        )}
        <Col>
          <div>
            <div style={{ display: "flex" }}>
              <span style={{ alignItems: "flex-start" }}>
                <h2>{props.feedback.title}</h2>
              </span>
            </div>
            <div className={styles.subtitle}>
              <span className={styles.subtitleLeft}>
                <div className={styles.badges}>
                  {!!props.feedback.authorData && (
                    <span>
                      <AuthorProfileBadge authoredBy={props.feedback.authorData} />
                    </span>
                  )}
                  {props.feedback.parent === FeedbackParent.Application ? (
                    <span>
                      <FeedbackAppBadge app={props.feedback.application} />
                    </span>
                  ) : null}
                  {props.feedback.parent === FeedbackParent.Topic ? (
                    <span>
                      <FeedbackTopicBadge topic={props.feedback.topic} />
                    </span>
                  ) : null}
                  <span>
                    {isCMUser && (
                      <div className={styles.feedbackIconDisplay}>
                        {props.feedback.isPrivate && (
                          <Icon
                            appName="GeneralLayout"
                            assetName="GENERAL_L_Lock"
                            className={styles.feedbackPrivateIcon}
                            title="This feedback is private and cannot be viewed by users from other companies"
                          />
                        )}
                        {!props.feedback.isPrivate && (
                          <Icon
                            appName="GeneralLayout"
                            assetName="GENERAL_L_Public_20"
                            className={styles.feedbackPrivateIcon}
                            title="This feedback is public and may be viewed by users from other companies"
                          />
                        )}
                      </div>
                    )}
                  </span>
                  <span>
                    {isCMUser && isFeatureRequest && (
                      <FeedbackSizeBadge size={(props.feedback as FeatureRequest)?.feedbackSize} />
                    )}
                  </span>
                  <span>
                    <FeedbackWorkflowStateBadge state={props.feedback.workflowState} />
                  </span>
                  <span>
                    <FeedbackCategoryBadge
                      category={
                        FeedbackCategory[props.feedback.category as keyof typeof FeedbackCategory]
                      }
                    />
                  </span>
                </div>
                <div className={styles.metadata}>
                  <small>
                    <span>
                      <span>Created on </span>
                      <DateDisplay date={new Date(props.feedback.dateCreated)} />
                    </span>

                    {props.feedback.dateModified != null && (
                      <span>
                        <span>Edited on </span>
                        <DateDisplay date={new Date(Date.parse(props.feedback.dateModified))} />
                      </span>
                    )}

                    {!props.static &&
                      !!props.feedback.permissions &&
                      props.feedback.permissions.canEdit && (
                        <span>
                          <Link
                            to={appPaths.feedback.editFeedback.replace(
                              ":code",
                              props.feedback.code
                            )}
                          >
                            Edit
                          </Link>
                        </span>
                      )}
                  </small>
                  {!props.static && !!props.onStateChanged && (
                    <span className={styles.buttonsSpan}>
                      <FeedbackStateChangeButtons
                        feedback={props.feedback}
                        onStateChange={props.onStateChanged}
                      />
                    </span>
                  )}
                </div>
              </span>
              <span className={styles.subtitleRight}>
                {!props.static &&
                  !!props.feedback.permissions &&
                  props.feedback.permissions.canMove && (
                    <span>
                      <Button
                        onClick={() => {
                          setIsMoveFeedbackModalOpen(true);
                        }}
                      >
                        <Icon
                          appName="GeneralLayout"
                          assetName="GENERAL_L_SignOut"
                          className={iconStyles.smallIcon}
                        />{" "}
                        Move
                      </Button>
                      <MoveFeedbackModal
                        feedback={props.feedback}
                        isModalOpen={isMoveFeedbackModalOpen}
                        toggleModalCallback={() =>
                          setIsMoveFeedbackModalOpen(!isMoveFeedbackModalOpen)
                        }
                        onSave={props.onMoveFeedback}
                      />
                    </span>
                  )}

                {!props.static &&
                  !!props.feedback.permissions &&
                  props.feedback.permissions.canViewHistory && (
                    <span>
                      <FeedbackHistoryModal
                        code={props.feedback.code}
                        triggerButton={
                          <Button>
                            <Icon
                              appName="GeneralLayout"
                              assetName="GENERAL_L_History"
                              className={iconStyles.smallIcon}
                            />{" "}
                            View History
                          </Button>
                        }
                      />
                    </span>
                  )}

                {!props.static &&
                  !!props.feedback.permissions &&
                  props.feedback.permissions.canForceTransition && (
                    <>
                      &nbsp;
                      <FeedbackForceTransitionStateButton
                        feedback={props.feedback}
                        onStateChange={props.onStateChanged}
                      />
                    </>
                  )}

                {!props.static && !!props.feedback.permissions && (
                  <>
                    &nbsp;
                    <FeedbackPrioritySetterContainer
                      feedbackCode={props.feedback.code}
                      existingPriority={props.feedback.priority}
                      saveImmediately
                      includeLabel
                      isReadonly={!props.feedback.permissions.canPrioritize}
                    />
                  </>
                )}
              </span>
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div
              style={{
                width: "100%",
                marginTop: "16px",
                paddingRight: "32px",
                borderRight: "1px solid #DCDCDC"
              }}
            >
              <div className={styles.tagContainer}>
                <TagDisplay selectedIds={props.feedback.tagIds} />
              </div>
              {props.children}
            </div>
            <div style={{ width: "248px", paddingLeft: "32px" }} className={styles.extraDataDiv}>
              <div>
                <div className={styles.sideContainer}>
                  <h4>Due Date</h4>
                  <DueDateDisplayContainer
                    feedbackCode={props.feedback.code}
                    dueDate={props.feedback.dueDate ? new Date(props.feedback.dueDate) : undefined}
                    enableEdit={
                      props.feedback.permissions ? props.feedback.permissions.canPrioritize : false
                    }
                    onChange={(_, date) => {
                      if (props.onDueDateChange) {
                        props.onDueDateChange(date);
                      }
                    }}
                  />
                </div>

                {isCMUser && (
                  <div className={styles.sideContainer}>
                    <h4>Assigned Users</h4>
                    <FeedbackAssignmentsDisplay
                      feedback={props.feedback}
                      onSaveChanges={(users: User[]) => {
                        if (props.onAssignedUsersChange) {
                          props.onAssignedUsersChange(users);
                        }
                      }}
                    />
                  </div>
                )}
                <div className={styles.sideContainer}>
                  <h4>Relationship</h4>
                  <RelationshipFeedbackDisplayContainer
                    feedback={props.feedback}
                    parent={getParentDisplay()}
                    childrenList={getChildrenDisplay()}
                    enableEdit
                    onRelationshipChange={props.onRelationshipChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

FeedbackDetails.defaultProps = {
  static: false
};
